import React from "react";
import Icon from "./AppIcon";
import { isChunkLoadError, reloadOnce } from "../lib/chunkReload";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    // Stale code-split chunk after a deploy: reload to the fresh entry instead
    // of showing a dead error screen (see lib/chunkReload).
    if (isChunkLoadError(error)) return { reloading: true };
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) { reloadOnce(); return; }
    error.__ErrorBoundary = true;
    window.__COMPONENT_ERROR__?.(error, errorInfo);
    // Verbose: surface the real cause in DevTools so users can copy/paste
    // when reporting an "Unexpected error" page.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    // Reload imminent (stale chunk) — render nothing to avoid flashing error UI.
    if (this.state?.reloading) return null;
    if (this.state?.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8 max-w-md">
            <div className="flex justify-center items-center mb-2 text-muted-foreground">
              <svg xmlns="http://www.w3.org/2000/svg" width="42px" height="42px" viewBox="0 0 32 33" fill="none">
                <path d="M16 28.5C22.6274 28.5 28 23.1274 28 16.5C28 9.87258 22.6274 4.5 16 4.5C9.37258 4.5 4 9.87258 4 16.5C4 23.1274 9.37258 28.5 16 28.5Z" stroke="currentColor" strokeWidth="2" strokeMiterlimit="10" />
                <path d="M11.5 15.5C12.3284 15.5 13 14.8284 13 14C13 13.1716 12.3284 12.5 11.5 12.5C10.6716 12.5 10 13.1716 10 14C10 14.8284 10.6716 15.5 11.5 15.5Z" fill="currentColor" />
                <path d="M20.5 15.5C21.3284 15.5 22 14.8284 22 14C22 13.1716 21.3284 12.5 20.5 12.5C19.6716 12.5 19 13.1716 19 14C19 14.8284 19.6716 15.5 20.5 15.5Z" fill="currentColor" />
                <path d="M21 22.5C19.9625 20.7062 18.2213 19.5 16 19.5C13.7787 19.5 12.0375 20.7062 11 22.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex flex-col gap-1 text-center">
              <h1 className="text-2xl font-medium text-foreground">Something went wrong</h1>
              <p className="text-muted-foreground text-base w-8/12 mx-auto">We encountered an unexpected error while processing your request.</p>
            </div>
            {this.state.error && (
              <details className="mt-4 text-left bg-muted rounded p-3 max-h-64 overflow-auto">
                <summary className="cursor-pointer text-sm font-medium text-foreground">
                  Show error details
                </summary>
                <pre className="text-xs text-error mt-2 whitespace-pre-wrap break-words">
                  {String(this.state.error?.message || this.state.error)}
                  {this.state.error?.stack ? '\n\n' + this.state.error.stack.split('\n').slice(0, 6).join('\n') : ''}
                </pre>
              </details>
            )}
            <div className="flex justify-center items-center gap-2 mt-6 flex-wrap">
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className="bg-primary hover:opacity-90 text-primary-foreground font-medium py-2 px-4 rounded flex items-center gap-2 transition-opacity duration-200 shadow-sm"
              >
                <Icon name="ArrowLeft" size={18} />
                Back
              </button>
              <button
                onClick={async () => {
                  // Hard reset for stuck users — kills any stale service worker
                  // + cache then redirects to root.
                  try {
                    if ('serviceWorker' in navigator) {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(regs.map(r => r.unregister()));
                    }
                    if ('caches' in window) {
                      const keys = await caches.keys();
                      await Promise.all(keys.map(k => caches.delete(k)));
                    }
                  } catch { /* ignore */ }
                  window.location.href = "/";
                }}
                className="bg-muted hover:bg-surface-3 text-foreground font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 shadow-sm border border-border"
              >
                Reset cache & reload
              </button>
            </div>
          </div >
        </div >
      );
    }

    return this.props?.children;
  }
}

export default ErrorBoundary;